import React, { FunctionComponent, useEffect } from "react";
import styles from "./Text.module.scss";
import TextProps from "./TextProps.interface";

import { motion, useAnimation } from "framer-motion";
import { useDispatch } from "react-redux";
import { useInView } from "react-intersection-observer";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import handleOpenModal from "blocks/helpers/handleOpenModal";
import Paragraph from "components/Paragraph/Paragraph";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import Button from "components/Button/Button";
import isBrowserContext from "helpers/isBrowserContext";

const Text: FunctionComponent<TextProps> = (props) => {
    const dispatch = useDispatch();

    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.3 });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    if (props.settings?.textAlignment) {
        wrapperClasses.push(styles[`alignment_${props.settings.textAlignment}`]);
    }

    if (props.settings?.textWidth) {
        wrapperClasses.push(styles[`width_${props.settings.textWidth}`]);
    }

    return (
        <motion.section className={combineClasses([styles.wrapper, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={styles.container}>
                {/* Tagline */}
                {props.content.tagline ? (
                    <span className={styles.tagline}>{props.content.tagline}</span>
                ) : null}

                {/* Headline */}
                {props.content.headline ? (
                    <DynamicHeading className={styles.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight}>{props.content.headline}</DynamicHeading>
                ) : null}

                {/* Introductory Text */}
                {props.content.introductoryText ? (
                    <Paragraph className={styles.intro_text} innerHTML={props.content.introductoryText} />
                ) : null}

                {/* Text */}
                {props.content.bodyText ? (
                    <Paragraph className={styles.text} innerHTML={props.content.bodyText} />
                ) : null}

                {/* Buttons */}
                {props.content.buttons && props.content.buttons.length > 0 ? (
                    <div className={styles.buttons}>
                        {props.content.buttons.map((button) => {
                            return (
                                <Button key={button.contentUdi} className={styles.button} modal={button.content.modal} onClick={() => { button.content.modal ? handleOpenModal(button.content.modal, button.content.stickyLink, dispatch) : undefined; }} to={button.content.link} parameters={button.content.parameters} anchor={button.content.anchor} settings={button.settings}>
                                    {button.content.label}
                                </Button>
                            );
                        })}
                    </div>
                ) : null}
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default Text;